import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, RotateCcw, UtensilsCrossed, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { BottomNav } from "@/components/BottomNav";
import { WEEKDAYS } from "@/data/defaultDiet";
import {
  loadDiet,
  setMealEntry,
  setMealEveryDay,
  renameMeal,
  addMeal,
  deleteMeal,
  setNote,
  resetDiet,
  todayIndex,
  starterIsEmpty,
} from "@/storage/diet";

// La dieta se ve de dos maneras: el día de hoy, que es lo que se consulta al
// abrir la app antes de comer, y la semana entera, que es la tabla de la que
// venía el usuario y sirve para hacer la compra.
function DietPage() {
  const [diet, setDiet] = useState(loadDiet);
  const [day, setDay] = useState(todayIndex);

  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [applyAll, setApplyAll] = useState(false);

  const [renaming, setRenaming] = useState(null);
  const [mealName, setMealName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [confirmReset, setConfirmReset] = useState(false);

  const today = todayIndex();
  // En casi todas las cuentas el punto de partida es una dieta en blanco, así
  // que el botón de restaurar es en realidad un vaciar y debe decirlo.
  const blankStarter = starterIsEmpty();
  const filled = useMemo(
    () => diet.meals.filter((meal) => (diet.plan[meal.id]?.[day] || "").trim()).length,
    [diet, day]
  );

  // ─── Celda ──────────────────────────────────────────────────────────────────
  const openEditor = (meal) => {
    setEditing(meal);
    setDraft(diet.plan[meal.id]?.[day] || "");
    setApplyAll(false);
  };

  const saveEditor = () => {
    if (!editing) return;
    const text = draft.trim();
    setDiet(
      applyAll
        ? setMealEveryDay(diet, editing.id, text)
        : setMealEntry(diet, editing.id, day, text)
    );
    setEditing(null);
    toast.success(applyAll ? `${editing.name} actualizado toda la semana` : "Guardado");
  };

  // ─── Comidas ────────────────────────────────────────────────────────────────
  const openRename = (meal) => {
    setRenaming(meal);
    setMealName(meal.name);
  };

  const saveRename = () => {
    const name = mealName.trim();
    if (!renaming || !name) return;
    setDiet(renameMeal(diet, renaming.id, name));
    setRenaming(null);
    toast.success("Comida renombrada");
  };

  const handleAddMeal = () => {
    const name = mealName.trim();
    if (!name) return;
    setDiet(addMeal(diet, name));
    setMealName("");
    setRenaming(null);
    toast.success(`"${name}" añadida`);
  };

  const handleDeleteMeal = () => {
    if (!confirmDelete) return;
    setDiet(deleteMeal(diet, confirmDelete.id));
    setConfirmDelete(null);
    toast.success("Comida eliminada");
  };

  const saveNote = () => {
    setDiet(setNote(diet, noteDraft.trim()));
    setNoteOpen(false);
  };

  const handleReset = () => {
    setDiet(resetDiet());
    setConfirmReset(false);
    toast.success(blankStarter ? "Dieta vaciada" : "Dieta restaurada");
  };

  return (
    <div className="min-h-screen bg-background pb-28">
      <div
        className="pointer-events-none fixed top-0 left-0 right-0 h-[400px] opacity-50"
        style={{ background: "radial-gradient(ellipse at 50% 0%, hsl(199 89% 56% / 0.06), transparent 70%)" }}
      />

      <div className="relative z-10 mx-auto max-w-2xl px-5 pt-8">
        <div className="flex items-start justify-between mb-6 gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-primary uppercase tracking-[0.15em]">Tu plan</p>
            <h1 className="text-foreground font-bold text-2xl tracking-tight mt-1">Dieta</h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setConfirmReset(true)}
            aria-label={blankStarter ? "Vaciar dieta" : "Restaurar dieta"}
            className="h-10 w-10 rounded-2xl glass-surface text-muted-foreground hover:text-foreground flex-shrink-0"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>

        <Tabs defaultValue="dia" className="w-full min-w-0">
          <TabsList className="w-full bg-secondary/30 rounded-xl h-10 p-1 mb-5">
            <TabsTrigger
              value="dia"
              className="flex-1 rounded-lg text-xs font-semibold data-[state=active]:bg-primary/15 data-[state=active]:text-primary"
            >
              Día
            </TabsTrigger>
            <TabsTrigger
              value="semana"
              className="flex-1 rounded-lg text-xs font-semibold data-[state=active]:bg-primary/15 data-[state=active]:text-primary"
            >
              Semana
            </TabsTrigger>
          </TabsList>

          {/* ── Día ── */}
          <TabsContent value="dia" className="mt-0 space-y-4">
            <div className="flex gap-1.5 overflow-x-auto fp-scrollbar pb-1 -mx-1 px-1">
              {WEEKDAYS.map((weekday, i) => {
                const active = i === day;
                return (
                  <motion.button
                    key={weekday.id}
                    whileTap={{ scale: 0.94 }}
                    onClick={() => setDay(i)}
                    aria-label={weekday.name}
                    style={
                      active
                        ? {
                            background: "hsl(var(--primary))",
                            boxShadow: "0 0 20px hsl(var(--primary) / 0.30), 0 2px 8px rgba(0,0,0,0.4)",
                          }
                        : { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.06)" }
                    }
                    className={`relative flex-1 min-w-[42px] h-11 rounded-2xl text-[12px] font-bold uppercase tracking-[0.06em]
                      transition-colors duration-200 ${active ? "text-white" : "text-white/35 hover:text-white/60"}`}
                  >
                    {weekday.short}
                    {/* Hoy se marca aunque estés mirando otro día, para no perder la referencia. */}
                    {i === today && (
                      <span
                        className={`absolute bottom-1.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full ${
                          active ? "bg-white/70" : "bg-primary"
                        }`}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>

            <div className="flex items-baseline justify-between gap-2 px-0.5">
              <p className="text-sm font-semibold text-foreground">
                {WEEKDAYS[day].name}
                {day === today && <span className="text-primary text-[11px] font-bold ml-2 uppercase tracking-wider">Hoy</span>}
              </p>
              <p className="text-[11px] text-muted-foreground tabular-nums">
                {filled} de {diet.meals.length} comidas
              </p>
            </div>

            <div className="space-y-2">
              {diet.meals.map((meal) => {
                const text = diet.plan[meal.id]?.[day] || "";
                return (
                  <div key={meal.id} className="glass-surface rounded-2xl p-3.5 flex items-start gap-3">
                    <span className="h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: "rgba(10,133,255,0.12)", border: "1px solid rgba(10,133,255,0.22)" }}>
                      <UtensilsCrossed className="h-4 w-4 text-primary" />
                    </span>

                    <button onClick={() => openEditor(meal)} className="flex-1 min-w-0 text-left">
                      <span className="block text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                        {meal.name}
                      </span>
                      <span className={`block text-sm mt-0.5 leading-snug ${text ? "text-foreground" : "text-muted-foreground/50 italic"}`}>
                        {text || "Sin definir · toca para añadir"}
                      </span>
                    </button>

                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button
                        onClick={() => openRename(meal)}
                        aria-label={`Renombrar ${meal.name}`}
                        className="h-7 w-7 rounded-lg flex items-center justify-center text-white/25 hover:text-white/70 hover:bg-white/8 transition-colors"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                      {diet.meals.length > 1 && (
                        <button
                          onClick={() => setConfirmDelete(meal)}
                          aria-label={`Eliminar ${meal.name}`}
                          className="h-7 w-7 rounded-lg flex items-center justify-center text-white/25 hover:text-destructive hover:bg-white/8 transition-colors"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => { setRenaming({ id: null, name: "" }); setMealName(""); }}
              className="w-full h-11 rounded-2xl border border-dashed border-border/60 text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors duration-200 flex items-center justify-center gap-2 text-sm font-semibold"
            >
              <Plus className="h-4 w-4" />
              Añadir comida
            </button>
          </TabsContent>

          {/* ── Semana ── */}
          <TabsContent value="semana" className="mt-0">
            {/* La tabla se desplaza dentro de su caja: siete columnas de texto no
                caben en un móvil y, sin esto, arrastrarían la página entera. */}
            <div className="glass-surface rounded-2xl p-1 overflow-x-auto fp-scrollbar min-w-0">
              <table className="w-full border-collapse" style={{ minWidth: 620 }}>
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-card text-left text-[9px] font-bold uppercase tracking-[0.1em] text-muted-foreground px-2.5 py-2">
                      Comida
                    </th>
                    {WEEKDAYS.map((weekday, i) => (
                      <th
                        key={weekday.id}
                        className={`text-left text-[9px] font-bold uppercase tracking-[0.1em] px-2.5 py-2 ${
                          i === today ? "text-primary" : "text-muted-foreground"
                        }`}
                      >
                        {weekday.short}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {diet.meals.map((meal) => (
                    <tr key={meal.id} className="border-t border-border/30">
                      <th className="sticky left-0 z-10 bg-card text-left text-[10px] font-bold uppercase tracking-[0.08em] text-foreground/80 px-2.5 py-2.5 align-top whitespace-nowrap">
                        {meal.name}
                      </th>
                      {WEEKDAYS.map((weekday, i) => (
                        <td key={weekday.id} className="px-2.5 py-2.5 align-top">
                          <button
                            onClick={() => { setDay(i); openEditor(meal); }}
                            className={`text-left text-[11px] leading-snug w-[150px] ${
                              diet.plan[meal.id]?.[i] ? "text-foreground/85" : "text-muted-foreground/40 italic"
                            }`}
                          >
                            {diet.plan[meal.id]?.[i] || "—"}
                          </button>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>

        {/* Nota al pie: el cheat meal y lo que el usuario quiera recordar. */}
        <button
          onClick={() => { setNoteDraft(diet.note); setNoteOpen(true); }}
          className="w-full text-left glass-surface rounded-2xl p-3.5 mt-4 flex items-start gap-3"
        >
          <span className="h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(10,133,255,0.12)", border: "1px solid rgba(10,133,255,0.22)" }}>
            <CalendarDays className="h-3.5 w-3.5 text-primary" />
          </span>
          <span className="min-w-0">
            <span className="block text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Nota</span>
            <span className={`block text-sm mt-0.5 leading-snug ${diet.note ? "text-foreground" : "text-muted-foreground/50 italic"}`}>
              {diet.note || "Sin nota · toca para escribir"}
            </span>
          </span>
        </button>
      </div>

      <BottomNav />

      {/* ── Editar celda ── */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent className="glass-surface rounded-3xl border-0 w-[calc(100vw-2rem)] max-w-sm">
          <DialogHeader className="text-left">
            <DialogTitle className="text-foreground">{editing?.name}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {WEEKDAYS[day].name}
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            autoFocus
            placeholder="Ej: Pollo (250 g) + arroz (100 g)"
            className="rounded-xl bg-secondary/30 border-border/40 text-foreground text-sm focus-visible:ring-primary/40 placeholder:text-muted-foreground/40 resize-none"
          />

          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={applyAll}
              onChange={(e) => setApplyAll(e.target.checked)}
              className="h-4 w-4 rounded accent-primary"
            />
            <span className="text-xs text-muted-foreground">Aplicar a los siete días</span>
          </label>

          <DialogFooter className="flex-row gap-2">
            <Button variant="ghost" onClick={() => setEditing(null)}
              className="flex-1 rounded-xl glass-surface text-foreground/80 hover:text-foreground hover:bg-secondary/60">
              Cancelar
            </Button>
            <Button onClick={saveEditor}
              className="flex-1 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Renombrar o añadir comida ── */}
      <Dialog open={!!renaming} onOpenChange={(open) => { if (!open) setRenaming(null); }}>
        <DialogContent className="glass-surface rounded-3xl border-0 w-[calc(100vw-2rem)] max-w-sm">
          <DialogHeader className="text-left">
            <DialogTitle className="text-foreground">{renaming?.id ? "Renombrar comida" : "Añadir comida"}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {renaming?.id ? "Cambia cómo se llama en toda la semana." : "Se añade vacía en los siete días."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.08em]">Nombre</Label>
            <Input
              value={mealName}
              onChange={(e) => setMealName(e.target.value)}
              placeholder="Ej: Recena"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && (renaming?.id ? saveRename() : handleAddMeal())}
              className="h-11 rounded-xl bg-secondary/30 border-border/40 text-foreground font-medium focus-visible:ring-primary/40 placeholder:text-muted-foreground/40"
            />
          </div>

          <Button
            onClick={renaming?.id ? saveRename : handleAddMeal}
            disabled={!mealName.trim()}
            className="w-full h-11 rounded-2xl bg-primary text-primary-foreground font-bold text-sm fp-glow hover:bg-primary/90 disabled:opacity-40 disabled:shadow-none"
          >
            {renaming?.id ? "Guardar" : "Añadir"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* ── Nota ── */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="glass-surface rounded-3xl border-0 w-[calc(100vw-2rem)] max-w-sm">
          <DialogHeader className="text-left">
            <DialogTitle className="text-foreground">Nota</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Lo que quieras tener presente junto a la dieta.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            rows={3}
            autoFocus
            className="rounded-xl bg-secondary/30 border-border/40 text-foreground text-sm focus-visible:ring-primary/40 resize-none"
          />
          <DialogFooter className="flex-row gap-2">
            <Button variant="ghost" onClick={() => setNoteOpen(false)}
              className="flex-1 rounded-xl glass-surface text-foreground/80 hover:text-foreground hover:bg-secondary/60">
              Cancelar
            </Button>
            <Button onClick={saveNote}
              className="flex-1 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Eliminar comida ── */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <DialogContent className="glass-surface rounded-3xl border-0 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">Eliminar comida</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              ¿Eliminar &quot;{confirmDelete?.name}&quot;? Se borra en los siete días.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2">
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}
              className="flex-1 rounded-xl glass-surface text-foreground/80 hover:text-foreground hover:bg-secondary/60">
              Cancelar
            </Button>
            <Button onClick={handleDeleteMeal}
              className="flex-1 rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Vaciar o restaurar ── */}
      <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
        <DialogContent className="glass-surface rounded-3xl border-0 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">{blankStarter ? "Vaciar dieta" : "Restaurar dieta"}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {blankStarter
                ? "Se borrará todo lo que hayas escrito y la dieta quedará en blanco. ¿Continuar?"
                : "Vuelve a tu dieta original y descarta los cambios que hayas hecho. ¿Continuar?"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2">
            <Button variant="ghost" onClick={() => setConfirmReset(false)}
              className="flex-1 rounded-xl glass-surface text-foreground/80 hover:text-foreground hover:bg-secondary/60">
              Cancelar
            </Button>
            <Button onClick={handleReset}
              className="flex-1 rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {blankStarter ? "Vaciar" : "Restaurar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DietPage;
